import type { FC } from 'react';
import { useMemo } from 'react';
import { HStack, VStack, useBreakpointValue } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { Text } from '../../../ui-components';
import type { TextSize } from '../../../ui-components';
import type { SearchPrices } from '../hooks/use-search/use-search-types';

export interface SelectedVariant {
    id: string;
    c_pwaUrl: string;
    c_imageUrl: string;
}

const CurrencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

interface PlpProductPriceProps {
    id: string;
    isSingleVariant: boolean;
    searchPrices: SearchPrices;
}

export const PlpProductPrice: FC<PlpProductPriceProps> = ({
    id,
    isSingleVariant,
    searchPrices: { price, regularPrice, salePrice, suggestedPrice } = {
        regularPrice: [0],
        price: [0],
    },
}) => {
    const hasSuggestedPrice = suggestedPrice && suggestedPrice.length > 0;
    const saleHighlight: [number, number] | [number] | undefined = salePrice ? price : undefined;
    const saleStrikeout: [number, number] | [number] = useMemo(() => {
        const values = [...(regularPrice || price)];
        if (suggestedPrice) {
            values.push(...suggestedPrice);
        }
        const min = Math.min(...values);
        const max = Math.max(...values);
        if (min === max) {
            return [min];
        }
        return [min, max];
    }, [suggestedPrice, regularPrice, price]);

    const priceTextSize = useBreakpointValue<TextSize>({ base: 'small', md: 'base' }) ?? 'small';
    const subPriceTextSize = useBreakpointValue<TextSize>({ base: 'xs', md: 'small' }) ?? 'xs';
    const suggestedHighlight: [number, number] | [number] = price;
    const suggestedStrikeout: [number, number] | [number] | undefined = suggestedPrice;

    if (saleHighlight) {
        return (
            <section data-testid={`product-${id}-price-container`}>
                <VStack spacing={1} alignItems="flex-start">
                    <Text
                        color="slt-red.1000"
                        as="h4"
                        style="bold"
                        size={priceTextSize}
                        id={`product-${id}-sale-price`}
                    >
                        {saleHighlight.map((p) => CurrencyFormatter.format(p)).join(' - ')}
                    </Text>

                    {saleStrikeout ? (
                        <HStack spacing={1} alignItems="center">
                            {hasSuggestedPrice && (
                                <Text
                                    id={`product-${id}-suggested-price-label`}
                                    size={subPriceTextSize}
                                    color="slt-pepper.900"
                                >
                                    <FormattedMessage id="suggested.price" defaultMessage="Sugg." />
                                </Text>
                            )}

                            <Text
                                size={subPriceTextSize}
                                type="cross"
                                color="slt-pepper.900"
                                id={`product-${id}-sale-price-strikeout`}
                            >
                                {isSingleVariant
                                    ? CurrencyFormatter.format(
                                          saleStrikeout[saleStrikeout.length - 1],
                                      )
                                    : saleStrikeout
                                          .map((p) => CurrencyFormatter.format(p))
                                          .join(' - ')}
                            </Text>
                        </HStack>
                    ) : null}
                </VStack>
            </section>
        );
    }

    if (suggestedHighlight) {
        return (
            <section data-testid={`product-${id}-price-container`}>
                <VStack spacing={1} alignItems="flex-start">
                    <Text
                        color="slt-pepper.1000"
                        as="h4"
                        style="bold"
                        size={priceTextSize}
                        id={`product-${id}-price`}
                    >
                        {suggestedHighlight.map((p) => CurrencyFormatter.format(p)).join(' - ')}
                    </Text>

                    {suggestedStrikeout ? (
                        <HStack spacing={1} alignItems="center">
                            <Text
                                id={`product-${id}-suggested-price-label`}
                                size={subPriceTextSize}
                                color="slt-pepper.900"
                            >
                                <FormattedMessage id="suggested.price" defaultMessage="Sugg." />
                            </Text>

                            <Text
                                size={subPriceTextSize}
                                type="cross"
                                color="slt-pepper.900"
                                id={`product-${id}-price-strikeout`}
                            >
                                {suggestedStrikeout
                                    ?.map((p) => CurrencyFormatter.format(p))
                                    .join(' - ')}
                            </Text>
                        </HStack>
                    ) : null}
                </VStack>
            </section>
        );
    }

    return null;
};
